import ImageIcon from '@mui/icons-material/Image';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React from 'react';

import FormField from '@components/Common/FormField';

import './GuideImagePicker.scss';

export interface PickedGuideImage {
  url: string;
  alt: string;
  caption: string;
}

interface GuideImagePickerProps {
  /** Slug of the guide being edited, used only to name the stored object readably. */
  slug: string;
  /** What to call the button. */
  label: string;
  /** Called once the image is stored and the author has described it. */
  onPicked: (image: PickedGuideImage) => void;
  /** Hidden for a hero image, where a caption has nowhere to render. */
  withCaption?: boolean;
  disabled?: boolean;
  /** Render the trigger as an icon button, with `label` as its tooltip and accessible name. */
  iconOnly?: boolean;
  /** The icon for `iconOnly`; defaults to the image glyph. */
  icon?: React.ReactNode;
}

/**
 * Upload one image and describe it.
 *
 * **The alt text is collected here, before the image reaches the document, and that is the whole
 * design.** `checkGuideHtml` refuses to publish a body containing an `<img>` with no alt, so an
 * insert-then-describe flow produces a guide that is blocked the moment the picture lands and
 * leaves the author to work out why. Worse, the import path already learned this the expensive
 * way: the alt-text rule was the most likely reason the model deleted an image outright, because
 * deleting was the cheapest way to satisfy it. Asking first is what keeps the rule from being the
 * thing that destroys the pictures it exists to protect.
 *
 * The upload is a plain multipart POST to `/api/guides/image`, which reuses the import pipeline's
 * own store: same bucket, same policy check, same verification that a reader can actually fetch
 * the object. Nothing about storage is decided here.
 */
/** Matches GUIDE_IMAGE_MAX_BYTES on the server, so the rejection happens before the upload. */
const MAX_BYTES = 8 * 1024 * 1024;
const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif';

const GuideImagePicker: React.FC<GuideImagePickerProps> = ({
  slug,
  label,
  onPicked,
  withCaption = false,
  disabled = false,
  iconOnly = false,
  icon,
}) => {
  // A real id, so the <label> can be associated with the input instead of an imperative
  // ref.click(). Two of these render at once in the shell editor (the inspector's hero field and
  // the body toolbar), and a shared id would point both labels at the first input.
  const inputId = `guide-image-${React.useId()}`;
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pending, setPending] = React.useState<string | null>(null);
  const [alt, setAlt] = React.useState('');
  const [caption, setCaption] = React.useState('');

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset first, so picking the same file twice in a row still fires.
    event.target.value = '';
    if (!file) return;

    setError('');

    /*
     * Checked here as well as on the server, because the server can only answer once it has the
     * whole file. Without this an author waits out the upload of a 40MB phone photo to be told no.
     */
    if (file.size > MAX_BYTES) {
      setError(
        `That image is ${Math.round(file.size / 1024 / 1024)}MB, over the ${MAX_BYTES / 1024 / 1024}MB limit. Resize it first: a photo that big is a slow page for a reader on a phone.`,
      );
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('slug', slug || 'guide');
      const { data } = await axios.post('/api/guides/image', form);
      setPending(data.url);
      setAlt('');
      setCaption('');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not upload that image.');
    } finally {
      setUploading(false);
    }
  };

  const confirm = () => {
    if (!pending || !alt.trim()) return;
    onPicked({ url: pending, alt: alt.trim(), caption: caption.trim() });
    setPending(null);
    setAlt('');
    setCaption('');
  };

  return (
    <>
      {/* The input is the control and the button is its label, rather than a button that reaches
          into a hidden input with ref.click(). `hidden` removes an element from the accessibility
          tree, so the aria-label this used to carry was never read by anything. */}
      <span className="ge-image-picker">
        <input
          id={inputId}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={onFile}
          disabled={disabled || uploading}
        />
        {iconOnly ? (
          // The span gives the tooltip something to anchor to while the button is disabled mid-upload.
          <Tooltip title={uploading ? 'Uploading' : label}>
            <span className="ge-image-picker__icon">
              <IconButton
                component="label"
                htmlFor={inputId}
                aria-label={label}
                disabled={disabled || uploading}
              >
                {uploading ? <CircularProgress size={16} /> : (icon ?? <ImageIcon />)}
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            component="label"
            htmlFor={inputId}
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon />}
            disabled={disabled || uploading}
          >
            {uploading ? 'Uploading' : label}
          </Button>
        )}
      </span>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Dialog open={!!pending} onClose={() => setPending(null)} fullWidth maxWidth="sm">
        <DialogTitle>Describe this image</DialogTitle>
        <DialogContent>
          {pending && (
            <Box
              component="img"
              src={pending}
              alt=""
              sx={{ width: '100%', maxHeight: 240, objectFit: 'contain', mb: 2 }}
            />
          )}
          <Box sx={{ mb: 2 }}>
            <FormField
              required
              name="alt"
              label="Alt text"
              value={alt}
              onChange={(_, v) => setAlt(String(v ?? ''))}
              helperText="What the image shows, for a reader who cannot see it. A guide will not publish without this."
            />
          </Box>
          {withCaption && (
            <FormField
              required={false}
              name="caption"
              label="Caption (optional)"
              value={caption}
              onChange={(_, v) => setCaption(String(v ?? ''))}
              helperText="Printed under the image. Leave blank for no caption."
            />
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 2 }}>
            The image is already stored and publicly readable. Cancelling leaves it unused rather
            than deleting it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={!alt.trim()}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GuideImagePicker;
